import { Capacitor } from "@capacitor/core";

export type AdSenseConfig = {
  enabled: boolean;
  client: string;
  bannerSlot: string;
  consentManaged: boolean;
  preview: boolean;
};

export interface AdsAdapter {
  initialize(): Promise<boolean>;
  displayBanner(container: HTMLElement): void;
  hideBanner(container: HTMLElement): void;
}

export function readAdSenseConfig(env: ImportMetaEnv): AdSenseConfig {
  return {
    enabled: env.VITE_ADSENSE_ENABLED === "true",
    client: env.VITE_ADSENSE_CLIENT ?? "",
    bannerSlot: env.VITE_ADSENSE_BANNER_SLOT ?? "",
    consentManaged: env.VITE_ADSENSE_CONSENT_MANAGED === "true",
    preview: env.VITE_ADSENSE_PREVIEW !== "false",
  };
}

export function shouldShowAdPreview(config: Pick<AdSenseConfig, "preview">, hostname: string, isDevelopment: boolean): boolean {
  if (!config.preview) return false;
  if (isDevelopment) return true;
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

export class NoopAdsAdapter implements AdsAdapter {
  async initialize(): Promise<boolean> { return false; }
  displayBanner(container: HTMLElement): void { container.hidden = true; }
  hideBanner(container: HTMLElement): void { container.hidden = true; }
}

declare global {
  interface Window { adsbygoogle?: unknown[]; }
}

export class BrowserAdSenseAdapter implements AdsAdapter {
  private loaded = false;

  constructor(private readonly config: AdSenseConfig) {}

  async initialize(): Promise<boolean> {
    if (!this.canLoad()) return false;
    if (document.querySelector('script[data-treasure-grid-adsense="true"]')) {
      this.loaded = true;
      return true;
    }
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.async = true;
      script.crossOrigin = "anonymous";
      script.dataset.treasureGridAdsense = "true";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(this.config.client)}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("AdSense script failed to load"));
      document.head.append(script);
    });
    this.loaded = true;
    return true;
  }

  displayBanner(container: HTMLElement): void {
    if (!this.loaded || !this.config.bannerSlot) {
      this.displayPreview(container);
      return;
    }
    container.hidden = false;
    if (container.dataset.adInitialized === "true") return;
    const unit = document.createElement("ins");
    unit.className = "adsbygoogle";
    unit.style.display = "block";
    unit.dataset.adClient = this.config.client;
    unit.dataset.adSlot = this.config.bannerSlot;
    unit.dataset.adFormat = "auto";
    unit.dataset.fullWidthResponsive = "true";
    container.append(unit);
    window.adsbygoogle = window.adsbygoogle ?? [];
    window.adsbygoogle.push({});
    container.dataset.adInitialized = "true";
  }

  hideBanner(container: HTMLElement): void {
    container.hidden = true;
  }

  private canLoad(): boolean {
    return this.config.enabled &&
      this.config.consentManaged &&
      !Capacitor.isNativePlatform() &&
      /^ca-pub-\d{16}$/.test(this.config.client) &&
      /^\d{10}$/.test(this.config.bannerSlot);
  }

  private displayPreview(container: HTMLElement): void {
    const hostname = typeof window === "undefined" ? "" : window.location.hostname;
    if (!shouldShowAdPreview(this.config, hostname, import.meta.env.DEV)) {
      container.hidden = true;
      return;
    }
    container.hidden = false;
    container.classList.add("ad-preview");
    if (container.querySelector(".ad-preview-content")) return;
    const preview = document.createElement("div");
    preview.className = "ad-preview-content";
    preview.innerHTML = `<strong>AdSense test placement</strong>
      <span>desktop-left-side-rail</span>
      <small>Shown locally after ads consent. Real ads load only on production with AdSense IDs.</small>`;
    container.append(preview);
  }
}
